import React, { FC, useState } from 'react'
import { useFormik, FormikProvider, Form } from 'formik'
import { useQueryParams } from 'src/hooks/ui'
import { useHeatIndexStandardListRequest } from 'src/hooks/api'
import { SearchParamsKeys, ContractorFilters } from 'src/enums'
import { User } from 'src/api'
import { Box, Chip, FormControl, Grid, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent } from '@mui/material'

const NONE_ASSIGNEE_VALUE = 'none'

interface ContractorListFiltersProps {
  activeFilters: ContractorFilters[]
  admins?: User[]
}

export const ContractorListFilters: FC<ContractorListFiltersProps> = ({ activeFilters, admins = [] }) => {
  const [queryParams, setQueryParams] = useQueryParams()
  const [hasSubscriptionStatusFilter] = useState(activeFilters.indexOf(ContractorFilters.Subscription) !== -1)
  const [hasHeatIndexStandardFilter] = useState(activeFilters.indexOf(ContractorFilters.HeatIndexStandard) !== -1)
  const [hasAssigneeFilter] = useState(activeFilters.indexOf(ContractorFilters.Assignee) !== -1)

  const { data: heatIndexStandards = [] } = useHeatIndexStandardListRequest({
    options: { enabled: hasHeatIndexStandardFilter }
  })

  const formik = useFormik({
    initialValues: {
      ...(hasSubscriptionStatusFilter ? { [SearchParamsKeys.SubscriptionStatus]: queryParams[SearchParamsKeys.SubscriptionStatus] || null } : {}),
      ...(hasHeatIndexStandardFilter ? { [SearchParamsKeys.HeatIndexStandardId]: queryParams[SearchParamsKeys.HeatIndexStandardId] || null } : {}),
      ...(hasAssigneeFilter ? { [SearchParamsKeys.AssigneeId]: queryParams[SearchParamsKeys.AssigneeId] || null } : {})
    },
    onSubmit: async (formState) => {
      const subscriptionStatus = formState[SearchParamsKeys.SubscriptionStatus] || null
      const heatIndexStandardId = formState[SearchParamsKeys.HeatIndexStandardId] || null
      const assigneeId = formState[SearchParamsKeys.AssigneeId] || null
      await setQueryParams(
        {
          ...(hasSubscriptionStatusFilter && subscriptionStatus ? { [SearchParamsKeys.SubscriptionStatus]: `${subscriptionStatus}` } : {}),
          ...(hasHeatIndexStandardFilter && heatIndexStandardId ? { [SearchParamsKeys.HeatIndexStandardId]: `${heatIndexStandardId}` } : {}),
          ...(hasAssigneeFilter && assigneeId ? { [SearchParamsKeys.AssigneeId]: `${assigneeId}` } : {})
        },
        [SearchParamsKeys.SubscriptionStatus, SearchParamsKeys.HeatIndexStandardId, SearchParamsKeys.AssigneeId]
      )
    }
  })

  const { submitForm, values, setValues } = formik

  const subscriptionStatusOptions = [
    { name: 'All', value: 'all' },
    { name: 'Paid plan', value: 'paidPlan' },
    { name: 'Trial is active', value: 'trialIsActive' },
    { name: 'Trial is ended', value: 'trialIsEnded' }
  ]

  const handleSubscriptionStatusChanges = (event: SelectChangeEvent<typeof values['subscriptionStatus']>) => {
    const {
      target: { value }
    } = event
    setValues({
      ...values,
      [SearchParamsKeys.SubscriptionStatus]: `${value}`
    })
    submitForm()
  }

  const handleHeatIndexStandardChanges = (event: SelectChangeEvent<string>) => {
    const {
      target: { value }
    } = event
    setValues({
      ...values,
      [SearchParamsKeys.HeatIndexStandardId]: value
    })
    submitForm()
  }

  const handleAssigneeChanges = (event: SelectChangeEvent<string>) => {
    const {
      target: { value }
    } = event
    setValues({
      ...values,
      [SearchParamsKeys.AssigneeId]: value
    })
    submitForm()
  }

  const getAssigneeLabel = (value: string): string => {
    if (value === NONE_ASSIGNEE_VALUE) return 'None'
    const admin = admins.find((item) => `${item.id}` === value)
    return admin?.name || ''
  }

  return (
    <FormikProvider value={formik}>
      <Form>
        <Grid container alignItems="center" spacing={4}>
          {hasSubscriptionStatusFilter && (
            <Grid item xs={12} sm="auto">
              <FormControl sx={{ mb: 1, width: { xs: '100%', sm: 400 } }}>
                <InputLabel id="subscription-status-chip-label">Subscription status</InputLabel>
                <Select
                  labelId="subscription-status-chip-label"
                  id="subscription-status-chip"
                  value={values[SearchParamsKeys.SubscriptionStatus] || ''}
                  onChange={handleSubscriptionStatusChanges}
                  input={<OutlinedInput id="select-chip" label="Subscription status" />}
                  renderValue={(selected) => {
                    const subscriptionItem = subscriptionStatusOptions.find((item) => `${item.value}` === selected)
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip key={`${subscriptionItem?.value}`} label={subscriptionItem?.name} />
                      </Box>
                    )
                  }}
                >
                  {subscriptionStatusOptions.map((subscriptionItem) => (
                    <MenuItem key={`${subscriptionItem?.value}`} value={`${subscriptionItem?.value}`}>
                      {subscriptionItem?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {hasHeatIndexStandardFilter && (
            <Grid item xs={12} sm="auto">
              <FormControl sx={{ mb: 1, width: { xs: '100%', sm: 400 } }}>
                <InputLabel id="heat-index-standard-chip-label">Heat index standard</InputLabel>
                <Select
                  labelId="heat-index-standard-chip-label"
                  id="heat-index-standard-chip"
                  value={`${values[SearchParamsKeys.HeatIndexStandardId] ?? ''}`}
                  onChange={handleHeatIndexStandardChanges}
                  input={<OutlinedInput id="select-heat-index-standard-chip" label="Heat index standard" />}
                  renderValue={(selected) => {
                    if (!selected) return <Box />
                    const standard = heatIndexStandards.find((item) => `${item.id}` === selected)
                    const label = standard ? `${standard.displayName || standard.name} (${standard.envType})` : ''
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip key={standard?.id} label={label} />
                      </Box>
                    )
                  }}
                >
                  <MenuItem key="all" value="">
                    All
                  </MenuItem>
                  {heatIndexStandards.map((standard) => (
                    <MenuItem key={standard.id} value={`${standard.id}`}>
                      {`${standard.displayName || standard.name} (${standard.envType})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {hasAssigneeFilter && (
            <Grid item xs={12} sm="auto">
              <FormControl sx={{ mb: 1, width: { xs: '100%', sm: 400 } }}>
                <InputLabel id="assignee-chip-label">Responsible person</InputLabel>
                <Select
                  labelId="assignee-chip-label"
                  id="assignee-chip"
                  value={`${values[SearchParamsKeys.AssigneeId] ?? ''}`}
                  onChange={handleAssigneeChanges}
                  input={<OutlinedInput id="select-assignee-chip" label="Responsible person" />}
                  renderValue={(selected) => {
                    if (!selected) return <Box />
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip key={selected} label={getAssigneeLabel(selected)} />
                      </Box>
                    )
                  }}
                >
                  <MenuItem key="all" value="">
                    All
                  </MenuItem>
                  <MenuItem key={NONE_ASSIGNEE_VALUE} value={NONE_ASSIGNEE_VALUE}>
                    None
                  </MenuItem>
                  {admins.map((admin) => (
                    <MenuItem key={admin.id} value={`${admin.id}`}>
                      {admin.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Form>
    </FormikProvider>
  )
}
